import { formatNumber as formatCount } from 'i18n/format';
import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { createContext, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { useTokens } from 'theme';
import { useWidgetData } from 'api/assistant';
import { FALLBACK_NOTES, widgetErrorText } from './copy';
import { formatAmount, formatShare } from './units';
import { TABLE_MAX_ROWS, buildPieData, formatValue, kpiValues, normalizeRows } from './widgetData';
import { buildEChartsOption } from './echartsOptions';
import EChart from './EChart';
import QuotableText from './QuotableText';
import useViewportVisibility from './useViewportVisibility';

const WidgetQuoteContext = createContext(undefined);

const titleOf = (columns, key) => columns.find((c) => c.key === key)?.title || key;
const widgetHeight = (height) => Number.isInteger(height) && height >= 160 && height <= 900 ? height : 300;

/** Every returned row; spreadsheet formulas remain text. */
export function widgetCsv(columns, rows) {
  const cell = (value) => {
    const text = value == null ? '' : String(value);
    const safe = /^[\s]*[=+\-@]/.test(text) && typeof value !== 'number' ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  return '\uFEFF' + [columns.map((c) => cell(c.title || c.key)).join(';'), ...rows.map((row) => columns.map((c) => cell(row[c.key])).join(';'))].join('\r\n');
}

function downloadRows(widget, columns, rows) {
  const url = URL.createObjectURL(new Blob([widgetCsv(columns, rows)], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  // Filesystem filenames cannot contain control characters.
  // eslint-disable-next-line no-control-regex
  anchor.download = `${(widget.title || translate('assistant.answerWidgetData')).replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')}.csv`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function WidgetNote({ children }) {
  useTranslation();
  const t = useTokens();
  return <Typography variant="caption" component="p" sx={{ display: 'block', color: t.inkMuted, m: 0, mt: 1 }}>{children}</Typography>;
}
function FallbackTable({ reason, columns, rows, height }) {
  useTranslation();
  return <><WidgetNote>{FALLBACK_NOTES()[reason] || FALLBACK_NOTES().shape}</WidgetNote><Box sx={{ mt: 1 }}><TableView columns={columns} rows={rows} height={height} /></Box></>;
}

/** Model options change presentation; the checked query supplies the data. */
const ChartView = React.memo(function ChartView({ chart, columns, rows, height }) {
  const { t: translate, i18n } = useTranslation();
  const t = useTokens();
  const theme = useTheme();
  const chartTheme = useMemo(() => ({ ...t, fontFamily: theme.typography.fontFamily, locale: i18n.resolvedLanguage }), [t, theme.typography.fontFamily, i18n.resolvedLanguage]);
  const palette = t.series;
  const result = useMemo(() => buildEChartsOption(chart, columns, rows, palette, chartTheme), [chart, columns, rows, palette, chartTheme]);
  const circular = chart.kind === 'pie' || chart.kind === 'doughnut';
  const funnel = chart.kind === 'funnel';
  const pie = useMemo(() => circular || funnel ? buildPieData(chart, columns, rows, palette) : null, [circular, funnel, chart, columns, rows, palette]);
  if (result.error || !result.option) return <FallbackTable reason={result.error} columns={columns} rows={rows} height={height} />;
  const measures = [...new Set(chart.y.map((key) => titleOf(columns, key)))].join(', ');
  const label = circular ? translate('assistant.answerWidgetPieChartBy', { value1: measures, value2: titleOf(columns, chart.x) }) : translate('assistant.answerWidgetChart', { value1: measures, value2: chart.x ? translate('assistant.answerWidgetBy', { value1: titleOf(columns, chart.x) }) : '' });
  const fallback = <FallbackTable reason="shape" columns={columns} rows={rows} height={height} />;
  if (funnel && pie && !pie.fallback) return <FunnelChart option={result.option} pie={pie} height={height} label={label} fallback={fallback} />;
  const chartHeight = height ? widgetHeight(height) : chart.kind === 'heatmap'
    ? Math.max(300, Math.min(900, (result.option.yAxis?.[0]?.data?.length || 0) * 16 + 80)) : chart.kind === 'choropleth' ? 380 : 300;
  if (chart.kind === 'choropleth') return <>
    <EChart option={result.option} height={chartHeight} aria-label={translate('assistant.answerWidgetWorldMapByCountry', { value1: measures })} fallback={fallback} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
      <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: t.raised, border: `1px solid ${t.border}` }} />
      <Typography variant="caption" sx={{ color: t.inkMuted }}>{translate('assistant.answerWidgetNoData')}</Typography>
    </Box>
    {result.unmatched?.length > 0 && <Box sx={{ mt: 1.5 }}>
      <WidgetNote>{translate('assistant.answerWidgetCountriesThatCouldNotBeMatchedTo')}</WidgetNote>
      <TableView columns={columns} rows={result.unmatched} height={height} />
    </Box>}
  </>;
  return pie && !pie.fallback && !Array.isArray(result.option.legend) && result.option.legend?.show !== true && chart.options?.legend?.show !== false
    ? <PieChart option={result.option} pie={pie} height={height} label={label} fallback={fallback} />
    : <EChart option={result.option} height={chartHeight} aria-label={label} fallback={fallback} />;
});

/** Proportional shapes stay separate from readable, source-ordered stage values. */
function FunnelChart({ option, pie, height, label, fallback }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const chartHeight = widgetHeight(height);
  const series = option.series[0];
  const nativeLegend = (Array.isArray(option.legend) ? option.legend : [option.legend]).some((legend) => legend?.show === true);
  if (nativeLegend || series.label?.show === true) return <EChart option={option} height={chartHeight} aria-label={label} fallback={fallback} />;
  const labelStyle = series.label || {};
  const text = { color: typeof labelStyle.color === 'string' ? labelStyle.color : t.ink, fontFamily: labelStyle.fontFamily, fontSize: labelStyle.fontSize || 12, fontWeight: labelStyle.fontWeight || 400, lineHeight: '17px' };
  const inset = (value) => typeof value === 'number' ? value : /^\d+(?:\.\d+)?%$/.test(value) ? parseFloat(value) * chartHeight / 100 : 16;
  const stages = pie.slices.map((slice, index) => ({ slice, index }));
  if (['ascending', 'descending'].includes(series.sort)) stages.sort((a, b) => (a.slice.value - b.slice.value) * (series.sort === 'ascending' ? 1 : -1));
  return <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)', gap: 1.5, minWidth: 0 }}>
    <Box sx={{ minWidth: 0 }}><EChart option={option} height={chartHeight} aria-label={label} fallback={fallback} /></Box>
    <Box component="ol" aria-label={translate('assistant.answerWidgetStages', { value1: pie.title })} sx={{ listStyle: 'none', m: 0, paddingTop: `${inset(series.top)}px`, paddingBottom: `${inset(series.bottom)}px`, height: chartHeight, boxSizing: 'border-box', minWidth: 0, display: 'grid', gridTemplateRows: `repeat(${pie.slices.length}, minmax(0, 1fr))`, rowGap: `${series.gap}px` }}>
      {stages.map(({ slice, index: i }) => {
        const nativeColour = series.data[i]?.itemStyle?.color || option.color?.[i % option.color.length];
        return <Box component="li" key={i} aria-label={`${slice.label}: ${formatAmount(slice.value, pie.unit)}`} sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1 }}>
          <Box sx={{ minWidth: 0, display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <Box aria-hidden sx={{ width: 6, height: 6, flexShrink: 0, borderRadius: '50%', bgcolor: typeof nativeColour === 'string' ? nativeColour : slice.colour }} />
            <Typography component="span" sx={{ ...text, minWidth: 0, overflowWrap: 'anywhere' }}>{slice.label}</Typography>
          </Box>
          <Typography component="span" sx={{ ...text, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{formatAmount(slice.value, pie.unit)}</Typography>
        </Box>;
      })}
    </Box>
  </Box>;
}

/** One full-name legend, with the same category selection as the native legend. */
function PieChart({ option, pie, height, label, fallback }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const [selection, setSelection] = useState({ option: null, selected: {} });
  const selected = useMemo(() => {
    const values = { ...option.legend?.selected, ...(selection.option === option ? selection.selected : {}) };
    if (option.legend?.selectedMode !== 'single') return values;
    const active = pie.slices.find((slice) => values[slice.label] !== false) || pie.slices[0];
    return Object.fromEntries(pie.slices.map((slice) => [slice.label, slice.label === active.label]));
  }, [option, selection, pie]);
  const displayed = useMemo(() => ({ ...option, legend: { show: false, ...option.legend, selected } }), [option, selected]);
  const selectable = option.legend?.selectedMode !== false;
  const toggle = (name) => setSelection({ option, selected: option.legend?.selectedMode === 'single'
    ? Object.fromEntries(pie.slices.map((slice) => [slice.label, slice.label === name]))
    : { ...selected, [name]: selected[name] === false } });
  return <>
    <EChart option={displayed} height={height ? widgetHeight(height) : 240} aria-label={label} fallback={fallback} />
    <Box component="ul" aria-label={translate('assistant.answerWidgetLegend', { value1: pie.title })} sx={{ listStyle: 'none', m: 0, mt: 1, p: 0, maxHeight: 240, overflow: 'auto', scrollbarGutter: 'stable', borderTop: `1px solid ${t.border}` }}>
      {pie.slices.map((slice, i) => {
        const visible = selected[slice.label] !== false;
        const nativeColour = option.series?.[0]?.data?.[i]?.itemStyle?.color || option.color?.[i % option.color.length];
        const swatch = typeof nativeColour === 'string' ? nativeColour : slice.colour;
        return <Box component="li" key={i} sx={{ borderBottom: `1px solid ${t.border}`, '&:last-child': { borderBottom: 0 } }}>
          <Box component={selectable ? 'button' : 'div'} {...(selectable ? { type: 'button', 'aria-pressed': visible, onClick: () => toggle(slice.label) } : {})} sx={{
            display: 'grid', gridTemplateColumns: '8px minmax(0, 1fr) max-content 4.5em', alignItems: 'center', gap: 1,
            width: '100%', minWidth: 0, py: 0.75, px: 0.5, border: 0, borderRadius: '4px', background: 'transparent',
            fontFamily: 'inherit', fontSize: '0.8125rem', lineHeight: 1.5, textAlign: 'left', color: visible ? t.ink : t.inkMuted,
            cursor: selectable ? 'pointer' : 'default', '&:hover': selectable ? { bgcolor: t.raised } : {},
            '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: '-2px' },
          }}>
            <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: swatch, opacity: visible ? 1 : 0.35 }} />
            <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{slice.label}</Box>
            <Box component="span" sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatAmount(slice.value, pie.unit)}</Box>
            <Box component="span" sx={{ color: t.inkMuted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', textAlign: 'right' }}>{formatShare(slice.share)}</Box>
          </Box>
        </Box>;
      })}
    </Box>
  </>;
}

function KpiView({ chart, columns, rows, compact, height }) {
  useTranslation();
  const t = useTokens();
  const quoteSource = useContext(WidgetQuoteContext);
  const tiles = kpiValues(chart, columns, rows);
  if (tiles.fallback) return <FallbackTable reason={tiles.fallback} columns={columns} rows={rows} height={height} />;
  const style = chart.options?.textStyle || {};
  return <Box data-quote-source={quoteSource} sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, ...(height && { minHeight: widgetHeight(height), alignItems: 'center' }) }}>
    {tiles.map((tile) => <Box key={tile.key} sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: t.inkMuted, fontWeight: 600 }}>{tile.title}</Typography>
      <Typography sx={{ fontSize: typeof style.fontSize === 'number' ? style.fontSize : compact ? '1.35rem' : '2rem', fontWeight: style.fontWeight || 600, lineHeight: 1.2, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', color: style.color || t.ink, overflowWrap: 'anywhere' }}>{tile.value}</Typography>
    </Box>)}
  </Box>;
}

function TableView({ columns, rows, height }) {
  const { t: translate } = useTranslation();
  const quoteSource = useContext(WidgetQuoteContext);
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(TABLE_MAX_ROWS);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const direction = sort.direction === 'asc' ? 1 : -1;
    return rows.map((row, index) => ({ row, index })).sort((a, b) => {
      const x = a.row[sort.key];
      const y = b.row[sort.key];
      const compared = x == null ? (y == null ? 0 : 1) : y == null ? -1 : typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'ru', { numeric: true });
      return compared * direction || a.index - b.index;
    }).map(({ row }) => row);
  }, [rows, sort]);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1));
  const shown = sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  return <>
    <TableContainer data-quote-source={quoteSource} sx={{ maxHeight: height ? widgetHeight(height) : 420 }}><Table size="small" stickyHeader>
      <TableHead><TableRow>{columns.map((column) => <TableCell key={column.key} align={column.kind === 'measure' ? 'right' : 'left'} sortDirection={sort?.key === column.key ? sort.direction : false}>
        <TableSortLabel active={sort?.key === column.key} direction={sort?.key === column.key ? sort.direction : 'asc'} onClick={() => { setSort({ key: column.key, direction: sort?.key === column.key && sort.direction === 'asc' ? 'desc' : 'asc' }); setPage(0); }}>{column.title || column.key}</TableSortLabel>
      </TableCell>)}</TableRow></TableHead>
      <TableBody>{shown.map((row, i) => <TableRow key={i}>{columns.map((column) => <TableCell key={column.key} align={column.kind === 'measure' ? 'right' : 'left'}>{formatValue(row[column.key], column)}</TableCell>)}</TableRow>)}</TableBody>
    </Table></TableContainer>
    {rows.length > 25 && <TablePagination component="div" count={rows.length} page={currentPage} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100, TABLE_MAX_ROWS]} labelRowsPerPage={translate('assistant.answerWidgetRowsPerPage')} labelDisplayedRows={({ from, to, count }) => translate('assistant.answerWidgetOf', { value1: formatCount(from), value2: formatCount(to), value3: formatCount(count) })} getItemAriaLabel={(type) => type === 'next' ? translate('assistant.answerWidgetNextPage') : translate('assistant.answerWidgetPreviousPage')} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }} />}
  </>;
}

function WidgetBody({ widget, columns, rows, compact }) {
  const { t: translate } = useTranslation();
  if (!rows.length) return <Typography variant="body2" color="text.secondary">{translate('assistant.answerWidgetTheQueryReturnedNoRows')}</Typography>;
  if (widget.type === 'table' || !widget.chart) return <TableView columns={columns} rows={rows} height={widget.height} />;
  if (widget.chart.kind === 'kpi') return <KpiView chart={widget.chart} columns={columns} rows={rows} compact={compact} height={widget.height} />;
  return <ChartView chart={widget.chart} columns={columns} rows={rows} height={widget.height} />;
}

function LoadedAnswerWidget({ messageId, index, widget, width, filters, onReply, visible, minimumHeight }) {
  const { t: translate } = useTranslation();
  const query = useWidgetData(messageId, index, { ...(filters ? { filters } : {}), enabled: visible });
  const titleId = useId();
  const columns = useMemo(() => query.data?.columns || widget?.columns || [], [query.data, widget]);
  const rows = useMemo(() => normalizeRows(query.data?.rows, columns), [query.data, columns]);
  const isTable = Boolean(widget) && (widget.type === 'table' || !widget.chart);
  const quoteSource = useMemo(() => ({ kind: 'widget', widget_index: index }), [index]);
  const titleSource = useMemo(() => ({ kind: 'widget_title', widget_index: index }), [index]);
  const quoteLabel = widget?.title ? translate('assistant.answerWidgetWidgetData', { value1: widget.title }) : translate('assistant.answerWidgetWidgetData2');
  const canQuote = Boolean(onReply && query.isSuccess && query.data?.quote_proof);
  return <Paper component="section" variant="outlined" aria-labelledby={widget?.title ? titleId : undefined} sx={{ p: 2, width: '100%', minWidth: 0, minHeight: query.isSuccess ? undefined : minimumHeight, boxSizing: 'border-box' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: widget?.title ? 1.5 : 0 }}>
      {widget?.title && <Box sx={{ flex: 1, minWidth: 0 }}><QuotableText messageId={messageId} onReply={onReply} source={titleSource} label={translate('assistant.answerWidgetWidgetTitle')}><Typography id={titleId} component="h3" variant="subtitle2" sx={{ minWidth: 0, lineHeight: 1.5 }}>{widget.title}</Typography></QuotableText></Box>}
      {query.isSuccess && rows.length > 0 && isTable && <Tooltip title={translate('assistant.answerWidgetDownloadCSVData')}><IconButton size="small" aria-label={translate('assistant.answerWidgetDownloadCSVData')} onClick={() => downloadRows(widget, columns, rows)}><DownloadOutlined fontSize="small" /></IconButton></Tooltip>}
    </Box>
    {query.isLoading && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}><CircularProgress size={16} /><Typography variant="body2" color="text.secondary">{translate('assistant.answerWidgetLoadingData')}</Typography></Box>}
    {query.isError && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => query.refetch()}>{translate('assistant.answerWidgetRetry')}</Button>}>{widgetErrorText(query.error)}</Alert>}
    {query.isSuccess && widget && <QuotableText messageId={messageId} onReply={canQuote ? onReply : undefined} source={quoteSource} proof={query.data?.quote_proof} label={quoteLabel} markedOnly><WidgetQuoteContext.Provider value={canQuote ? JSON.stringify(quoteSource) : undefined}><WidgetBody widget={widget} columns={columns} rows={rows} compact={width === 'quarter'} /></WidgetQuoteContext.Provider></QuotableText>}
  </Paper>;
}

function reservedHeight(widget) {
  if (widget?.height) return widgetHeight(widget.height);
  if (widget?.chart?.kind === 'kpi') return 88;
  if (widget?.type === 'table' || !widget?.chart) return 160;
  return widget.chart.kind === 'choropleth' ? 380 : 300;
}

/** Offscreen cards detach their query observers so the shared cache can evict. */
export default React.memo(function AnswerWidget(props) {
  const { t: translate } = useTranslation();
  const target = useRef(null);
  const { visible } = useViewportVisibility(target, { messageId: props.messageId, widgetIndex: props.index });
  const measuredHeight = useRef(0);
  const visibility = useRef(visible);
  visibility.current = visible;
  useEffect(() => {
    if (!target.current || typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (visibility.current && entry?.contentRect.height) measuredHeight.current = entry.contentRect.height;
    });
    observer.observe(target.current);
    return () => observer.disconnect();
  }, []);
  const height = measuredHeight.current || reservedHeight(props.widget) + 64;
  return <Box ref={target} data-widget-index={props.index} sx={{ width: '100%', minWidth: 0, minHeight: visible ? reservedHeight(props.widget) + 64 : height }}>
    {visible ? <LoadedAnswerWidget {...props} width={props.width || 'full'} visible minimumHeight={height} /> : <Paper component="section" variant="outlined" aria-label={props.widget?.title || translate('assistant.answerWidgetWidget')} sx={{ p: 2, minHeight: height, boxSizing: 'border-box' }}>
      {props.widget?.title && <Typography component="h3" variant="subtitle2">{props.widget.title}</Typography>}
    </Paper>}
  </Box>;
});
